/**
 * AI Chat Service — calls the AI backend for conversational responses
 */

import { getAIChatUrl, getInferServiceUrl } from '../config/api';

export interface AIChatRequest {
    bot_id: string;
    message: string;
    conversation_history: Array<{ role: 'user' | 'assistant'; content: string }>;
    context: {
        business_name: string;
        service_of_interest?: string;
        turn_count: number;
        custom_instructions?: string;
        lead_capture_turn_threshold?: number;
    };
}

export interface AIChatResponse {
    message: string;
    ui: { type: 'options'; options: string[] } | null;
    action: 'request_lead_capture' | null;
    meta?: { turn_count: number; retrieved_chunks: number };
}

/**
 * Send a message to the AI chat backend and get a response.
 * Throws on network error or non-200 status.
 */
export async function sendAIMessage(request: AIChatRequest): Promise<AIChatResponse> {
    const url = getAIChatUrl();

    const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(request),
    });

    if (!response.ok) {
        const errorBody = await response.text().catch(() => '');
        throw new Error(`AI chat request failed (${response.status}): ${errorBody}`);
    }

    const data: AIChatResponse = await response.json();
    return data;
}

/**
 * Infer the user's service of interest from conversation history.
 * Returns the matched service name or "Unassigned".
 * Never throws — returns "Unassigned" on any failure.
 */
export async function inferService(
    botId: string,
    conversationHistory: Array<{ role: 'user' | 'assistant'; content: string }>
): Promise<string> {
    try {
        const url = getInferServiceUrl();
        const response = await fetch(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                bot_id: botId,
                conversation_history: conversationHistory,
            }),
        });

        if (!response.ok) {
            console.error(`[inferService] Request failed (${response.status})`);
            return 'Unassigned';
        }

        const data = await response.json();
        return data.service || 'General Enquiry';
    } catch (error) {
        console.error('[inferService] Error:', error);
        return 'General Enquiry';
    }
}
